




















































































































































































import {
  Component, Prop, PropSync, Watch,
} from 'vue-property-decorator';
import { EdititableUser, UserRoleObject } from '@/store/admin/types';
import {
  DetailedUserData, ManagedUsersTableData, UserManagement, UserProject,
} from '@/store/users/types';
import { UserPermission } from '@/store/userpermissions/types';
import { OrganizationData } from '@/store/organizations/types';
import { uuid } from 'vue-uuid';
import { REDZONE_ORGANIZATION_GUID } from '@/common/Constants';
import hasRedzoneEmail from '@/common/utils/UserUtils';
import CustomerDialogSelectOrganization from '@/components/Admin/Customers/CustomerDialogSelectOrganization.vue';
import {
  getRoleGuid,
  AuthUserRoles,
} from '../../../auth/roles';
import PermissionsMatrix from '../User/PermissionsMatrix.vue';
import AddProjectButton from './AddProjectButton.vue';
import AddCustomerButton from './AddCustomerButton.vue';
import AddManagedUserButton from './AddManagedUserButton.vue';
import EditUserCustomers from './EditUserCustomers.vue';
import EditUserProject from './EditUserProject.vue';
import EditUsersManagedUsers from './EditUsersManagedUsers.vue';
import AdminPanelMixin, { EditUserData } from '../AdminPanelMixin.vue';

@Component({
  components: {
    PermissionsMatrix,
    EditUserProject,
    EditUserCustomers,
    EditUsersManagedUsers,
    AddProjectButton,
    AddCustomerButton,
    AddManagedUserButton,
    CustomerDialogSelectOrganization,
  },
})
export default class UserEdit extends AdminPanelMixin {
  @Prop() readonly id!: string;

  @PropSync('selectedUser') readonly synchedSelectedUser: EditUserData;

  activeTab = 0;

  initCustomerGuids: string[] = null;

  initUserRoles: UserProject[] = null;

  initOrganization: OrganizationData = null;

  userOrganization: OrganizationData = null;

  isActive = true;

  isResettingOrg = false;

  addProjectDialog = false as boolean;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, arrow-parens
  requiredField = [(v) => !!v || v === 0 || 'This is a required field.'];

  isExpanded = [];

  fullName = '' as string;

  email = '' as string;

  showSnackbar = false as boolean;

  snackbarColor = '' as string;

  snackbarMessage = '' as string;

  userRole = '';

  currentRole = '';

  managedUsers = [] as ManagedUsersTableData[];

  managedUsersForRemoval = [] as ManagedUsersTableData[];

  managedUsersForAddition = [] as DetailedUserData[];

  managedUsersLoading = false;

  editConfirmDialog = false;

  currentUserManagedRoles: string[] = [];

  valid = true;

  get additionalOrgRules() {
    const orgRule = (v: OrganizationData | string | null | undefined): string | boolean => {
      if (v !== 'RedZone Robotics') {
        return 'RedZone org users cannot be System Admins for outside orgs';
      }
      return true;
    };

    const rules = [];
    if (hasRedzoneEmail(this.email)) {
      rules.push(orgRule);
    }
    return rules;
  }

  /**
   * @returns true if the user has the permission ADMIN_EDIT_USER_PAGE_VIEWCUSTOMERSTAB
   */
  hasPermissionAdminViewCustomersTab(): boolean {
    return this.hasPermission(UserPermission.ADMIN_EDIT_USER_PAGE_VIEWCUSTOMERSTAB);
  }

  /**
   * @returns true if the user has the permission ADMIN_EDIT_USER_PAGE_VIEWPROJECTSTAB
   */
  hasPermissionAdminViewProjectsTab(): boolean {
    return this.hasPermission(UserPermission.ADMIN_EDIT_USER_PAGE_VIEWPROJECTSTAB);
  }

  /**
   * @returns true if the user has the permission ADMIN_EDIT_USER_PAGE_VIEWMANAGEDUSERSTAB
   */
  hasPermissionAdminViewManagedUsersTab(): boolean {
    return this.hasPermission(UserPermission.ADMIN_EDIT_USER_PAGE_VIEWMANAGEDUSERSTAB);
  }

  /**
   * @returns true if the user has the permission ADMIN_ORGANIZATION_SECTION_CREATEANORGANIZATION
   */
  get hasPermissionAdminCreateAnOrganization(): boolean {
    return this.hasPermission(UserPermission.ADMIN_ORGANIZATION_SECTION_CREATEANORGANIZATION);
  }

  get isRedzoneEmail(): boolean {
    return hasRedzoneEmail(this.email);
  }

  /**
   * @returns true if the user's org is redzone
   */
  get isRedzoneOrg(): boolean {
    return this.userOrganization.guid === REDZONE_ORGANIZATION_GUID;
  }

  get tabOptions(): any[] {
    const tabOptions = [];
    if (this.hasPermissionAdminViewCustomersTab) {
      tabOptions.push({
        name: 'Customers',
        value: 'customers',
      });
    }
    if (this.hasPermissionAdminViewProjectsTab) {
      tabOptions.push({
        name: 'Projects',
        value: 'projects',
      });
    }
    if (this.hasPermissionAdminViewManagedUsersTab) {
      tabOptions.push({
        name: 'Managed Users',
        value: 'managedUsers',
      });
    }
    return tabOptions;
  }

  /**
   * @description Get the value from the current tab.
   * @returns {string} 'value' from the current tab
   */
  get activeTabValue(): string {
    if (this.tabOptions.length === 0) return '';
    return this.tabOptions[this.activeTab].value;
  }

  get roleItems(): string[] {
    if (!this.canManageUser) {
      return [this.currentRole];
    }
    return this.currentUserManagedRoles;
  }

  get canManageUser(): boolean {
    return this.currentUserManagedRoles?.includes(this.currentRole);
  }

  get shouldLimitOrganizations(): boolean {
    if (!this.$auth?.user?.email) {
      return true;
    }
    return !hasRedzoneEmail(this.$auth.user.email);
  }

  goToAddUser(): void {
    this.$router.push({ path: '/admin', query: { tab: 'users' } });
  }

  async saveUser(): Promise<void> {
    let userManagementTableNeedsUpdated = false;
    try {
      if (this.changes > 0) {
        userManagementTableNeedsUpdated = true;
      }
      const userEdit = this.createUser();
      await this.patchUser(userEdit);

      if (userEdit.email !== this.synchedSelectedUser.detailedUserData.email) {
        await this.$auth.setEmail(
          this.synchedSelectedUser.auth0Id,
          userEdit.email,
        );
      }

      // update state to match users new role
      this.userRole = this.currentRole;

      // delete managed users if any
      if (this.managedUsersForRemoval.length > 0) {
        userManagementTableNeedsUpdated = true;
        const guidsToRemoveOriginal = this.managedUsersForRemoval.map((u) => ({
          guid: u.guid,
          userManagementGuid: u.userManagementGuid,
        }));
        this.managedUsersForAddition = this.managedUsersForAddition.filter((u) => {
          const foundIndex = guidsToRemoveOriginal
            .findIndex((remove) => remove.guid === u.guid);
          if (foundIndex !== -1) {
            guidsToRemoveOriginal.splice(foundIndex, 1);
            return false;
          }
          return true;
        });
        await this.deleteUserManagement(guidsToRemoveOriginal
          .map((remove) => remove.userManagementGuid));
        this.managedUsersForRemoval.splice(0, this.managedUsersForRemoval.length);
      }

      // add managed users if any
      if (this.managedUsersForAddition.length > 0) {
        userManagementTableNeedsUpdated = true;
        const userManagements = [];
        for (let i = 0; i < this.managedUsersForAddition.length; i += 1) {
          const user = this.managedUsersForAddition[i];
          // find common customers between manager and managed user
          this.synchedSelectedUser.detailedUserData.customerGuids.filter(
            (cust) => user.customerGuids.includes(cust),
          ).forEach((customer) => {
            const newUserManagement: UserManagement = {
              guid: uuid.v4(),
              customerGuid: customer,
              subjectUserGuid: user.guid,
              managerUserGuid: this.synchedSelectedUser.detailedUserData.guid,
              organizationGuid: this.synchedSelectedUser.detailedUserData.organizationGuids[0],
            };
            userManagements.push(this.postUserManagement(newUserManagement));
          });
        }
        await Promise.all(userManagements);
        this.managedUsersForAddition.splice(0, this.managedUsersForAddition.length);
      }

      if (userManagementTableNeedsUpdated) {
        await this.updateAllUserManagements();
      }
      this.setInitCustomerData();
      this.setInitOrganizationData();
    } catch (ex) {
      this.snackbarColor = '#e61e25';
      this.snackbarMessage = (ex as Error).message;
      this.showSnackbar = true;
      this.$forceUpdate();
      return;
    }
    this.snackbarColor = 'green';
    this.snackbarMessage = 'Changes Saved';
    this.showSnackbar = true;
    this.$forceUpdate();
  }

  /**
   * @description Adds beforeunload listener to window
   */
  created(): void {
    window.addEventListener('beforeunload', this.beforeWindowUnload);
  }

  /**
   * @description Removes beforeunload listener to window
   */
  beforeDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  }

  /**
   * @description Show popup if user has unsaved changes
   */
  beforeWindowUnload(e): void {
    if (this.changes > 0) {
      // Cancel the event
      e.preventDefault();
      // Chrome requires returnValue to be set
      e.returnValue = '';
    }
  }

  setInitCustomerData(): void {
    this.initCustomerGuids = [
      ...this.synchedSelectedUser.detailedUserData.customerGuids,
    ];
    this.initUserRoles = [
      ...this.synchedSelectedUser.detailedUserData.userRoles,
    ];
    this.initUserRoles.forEach((ur) => {
      // eslint-disable-next-line no-param-reassign
      ur.startDate = ur.startDate ? new Date(String(ur.startDate).substring(0, 10)) : null;
      // eslint-disable-next-line no-param-reassign
      ur.endDate = ur.endDate ? new Date(String(ur.endDate).substring(0, 10)) : null;
    });
  }

  setInitOrganizationData(): void {
    this.initOrganization = this.organizationDataList.find(
      (o) => o.guid === this.synchedSelectedUser.detailedUserData.organizationGuids[0],
    );
    this.userOrganization = { ...this.initOrganization };
  }

  async setManagedUserRoles(): Promise<void> {
    const managedUserRoles = await this.getRolesCurrentUserCanManage();
    const authUserRoles = hasRedzoneEmail(this.email)
      ? AuthUserRoles
      : AuthUserRoles.filter((role) => !role.redzoneOnly);

    this.currentUserManagedRoles = managedUserRoles
      .map((x) => authUserRoles.find((r) => r.name === x)?.name)
      .filter((x) => !!x);
  }

  async mounted(): Promise<void> {
    this.setInitCustomerData();
    await this.fetchOrganizationData();
    await this.fetchCustomerData();
    await this.fetchAllDetailedUserData();
    await this.setManagedUserRoles();
    this.setInitOrganizationData();
    this.updateAllUserManagements();
    this.setUserRoles();
    if (
      this.synchedSelectedUser.detailedUserData
      && this.synchedSelectedUser.detailedUserData.firstname
      && this.synchedSelectedUser.detailedUserData.lastname
    ) {
      this.fullName = `${this.synchedSelectedUser.detailedUserData.firstname} ${this.synchedSelectedUser.detailedUserData.lastname}`;
    }
    this.email = this.synchedSelectedUser.detailedUserData.email;
    const dbUser = this.allUsers.find(
      (u) => u.useridentity
        === this.synchedSelectedUser.detailedUserData['useridentity'],
    );
    this.isActive = dbUser?.isActive ?? false;
  }

  @Watch('userOrganization')
  onUserOrganizationChange(): void {
    if (this.userOrganization != null && this.synchedSelectedUser != null
    && this.userOrganization.guid
    !== this.synchedSelectedUser.detailedUserData.organizationGuids[0]) {
      // open confirm dialog if user is in customers or projects
      if ((this.synchedSelectedUser.detailedUserData.customerGuids.length > 0
      || this.synchedSelectedUser.detailedUserData.userRoles.length > 0)
      && this.synchedSelectedUser.detailedUserData.organizationGuids.length > 0) {
        this.editConfirmDialog = true;
      } else {
        this.editOrganization();
      }
    }
    if (this.isRedzoneEmail && this.userOrganization.guid !== REDZONE_ORGANIZATION_GUID) {
      this.valid = false;
    } else {
      this.valid = true;
    }
  }

  @Watch('email')
  async onEmailChange(): Promise<void> {
    await this.setManagedUserRoles();

    if (!this.currentUserManagedRoles.includes(this.currentRole)) {
      [this.currentRole] = this.currentUserManagedRoles;
    }
  }

  @Watch('isRedzoneEmail')
  setRedZoneOrg(): void {
    if (this.isResettingOrg) return;
    const redzoneOrg: OrganizationData = this.organizationDataList.find(
      (o) => o.guid === REDZONE_ORGANIZATION_GUID,
    );
    this.userOrganization = redzoneOrg;
  }

  resetOrganization(): void {
    this.isResettingOrg = true;
    // reset selection back to previous organization
    if (hasRedzoneEmail(this.email)) {
      // revert back to previous non red-zone email
      this.email = this.synchedSelectedUser.detailedUserData.email;
    }
    this.userOrganization = this.organizationDataList.find(
      (o) => o.guid === this.synchedSelectedUser.detailedUserData.organizationGuids[0],
    );
    this.editConfirmDialog = false;
    this.$nextTick(() => {
      this.isResettingOrg = false;
    });
  }

  editOrganization(): void {
    // remove customers
    this.synchedSelectedUser.detailedUserData.customerGuids = [];
    // remove projects
    this.synchedSelectedUser.detailedUserData.userRoles = [];
    // set organization to dropdown selection
    this.synchedSelectedUser.detailedUserData.organizationGuids[0] = this.userOrganization.guid;
    this.editConfirmDialog = false;
  }

  getSynchedSelectedUserOrgName(): string {
    return this.organizationDataList.find(
      (o) => o.guid === this.synchedSelectedUser.detailedUserData.organizationGuids[0],
    )?.name;
  }

  createUser(): EdititableUser {
    const fullNameArray = this.fullName.split(' ');
    const userRoleObjects: UserRoleObject[] = this.synchedSelectedUser
      .detailedUserData.userRoles.map((role) => ({
        ProjectGuid: role.projectGuid,
        RoleGuid: getRoleGuid(this.currentRole),
        StartDate: role.startDate,
        EndDate: role.endDate,
      }));
    const user: EdititableUser = {
      Guid: this.id,
      Firstname: fullNameArray[0],
      Lastname: fullNameArray[1],
      userRoleObjects,
      isActive: this.isActive,
      email: this.email,
      organizationGuids:
        [this.userOrganization.guid],
      customerGuids: this.synchedSelectedUser.detailedUserData.customerGuids,
      roleGuid: getRoleGuid(this.currentRole),
    };
    return user;
  }

  async setUserRoles(): Promise<void> {
    if (!this.synchedSelectedUser?.detailedUserData?.guid) {
      throw new Error('Selected user detailed user data guid not found.');
    }
    await this.getUsersRole(this.synchedSelectedUser.detailedUserData.guid);
    const userRole = this.usersRoleResponse;

    if (!userRole) {
      return;
    }
    // eslint-disable-next-line prefer-destructuring
    this.userRole = userRole.description;
    this.currentRole = userRole.description;
  }

  async updateAllUserManagements(): Promise<void> {
    if (this.hasPermissionAdminViewManagedUsersTab) {
      this.managedUsers = [];
      this.managedUsersLoading = true;
      this.fetchAllUserManagements().then(() => {
        const { userManagements } = this.synchedSelectedUser.detailedUserData;

        if (userManagements.length > 0) {
          userManagements.forEach((userManagement) => {
            const existsInData = this.managedUsers
              .findIndex((u) => u.guid === userManagement.subjectUserGuid) !== -1;
            if (!existsInData) {
              const managedUser = this.allUsers
                .find((u) => u.guid === userManagement.subjectUserGuid);

              if (managedUser) {
                this.managedUsers.push({
                  guid: managedUser.guid,
                  userManagementGuid: userManagement.guid,
                  user: `${managedUser.firstname} ${managedUser.lastname}`,
                  email: managedUser.email ?? '',
                  role: managedUser.role,
                  organization: this.getOrganizationString(managedUser),
                  customers: this.getCustomersString(managedUser),
                });
              }
            }
          });
        }
        this.managedUsersLoading = false;
      });
    }
  }

  getOrganizationString(user: DetailedUserData): string {
    if (!user.organizationGuids || user.organizationGuids.length === 0) return '';

    const organization = this.organizationDataList.find(
      (o) => o.guid === user.organizationGuids[0],
    );

    return organization ? organization.name : '';
  }

  getCustomersString(user: DetailedUserData): string {
    if (!user.customerGuids || user.customerGuids.length === 0) return '';

    const customersString = user.customerGuids
      .map((guid) => this.customerDataList
        .find((c) => c.guid === guid)?.name ?? '');

    return customersString.filter((s) => s !== '').toString().replaceAll(',', ', ');
  }

  symmetricDifference(arr1: string[], arr2: string[]): number {
    return arr1
      .filter((x) => !arr2.includes(x))
      .concat(arr2.filter((x) => !arr1.includes(x))).length;
  }

  get changes(): number {
    if (!this.synchedSelectedUser) {
      return 0;
    }

    const orgDiffs = this.initOrganization != null && this.initOrganization.guid
      !== this.synchedSelectedUser.detailedUserData.organizationGuids[0] ? 1 : 0;

    const customerDiffs = this.initCustomerGuids
      ? this.symmetricDifference(
        this.initCustomerGuids,
        this.synchedSelectedUser.detailedUserData.customerGuids,
      )
      : 0;

    let dateDiffs = 0;
    let projectDiffs = 0;

    if (this.initUserRoles) {
      this.synchedSelectedUser.detailedUserData.userRoles.forEach((r) => {
        const foundUserRole = this.initUserRoles.find(
          (init) => init.projectGuid === r.projectGuid,
        );
        if (!foundUserRole) {
          return;
        }
        if (
          String(foundUserRole.endDate) !== String(r.endDate)
          || String(foundUserRole.startDate) !== String(r.startDate)
        ) {
          dateDiffs += 1;
        }
      });

      projectDiffs = this.symmetricDifference(
        this.initUserRoles.map((p) => p.guid),
        this.synchedSelectedUser.detailedUserData.userRoles.map((p) => p.guid),
      );
      projectDiffs += this.managedUsersForAddition.length;
      projectDiffs += this.managedUsersForRemoval.length;
    }

    return orgDiffs + customerDiffs + projectDiffs + dateDiffs;
  }
}
